// Where the device refresh secret lives (plan §10.2: OS secure storage). One small
// interface, one implementation per shell:
//   Electron   ElectronSecureStore: safeStorage (Windows DPAPI) via the preload; only the
//              ciphertext is kept (localStorage). Refuses to store when encryption is off.
//   Capacitor  CapacitorSecureStore: a Keychain (iOS) / Keystore (Android) plugin.
//   Web        IndexedDbSecureStore: DEVELOPMENT ONLY. A browser has no OS secure storage; the
//              secret sits in IndexedDB in clear. Fine for a dev tablet on the LAN, never for a
//              station in a shop (use the Electron or Capacitor build).
//   Tests      MemorySecureStore.

export interface SecureStore {
  readonly kind: 'electron' | 'capacitor' | 'indexeddb-dev' | 'memory';
  /** True when the platform does not really protect the value (browser dev builds). */
  readonly devOnly: boolean;
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
}

export class MemorySecureStore implements SecureStore {
  readonly kind = 'memory' as const;
  readonly devOnly = true;
  readonly #values = new Map<string, string>();

  async get(key: string) {
    return this.#values.get(key) ?? null;
  }
  async set(key: string, value: string) {
    this.#values.set(key, value);
  }
  async delete(key: string) {
    this.#values.delete(key);
  }
}

/** The safeStorage part of the Electron preload API (`window.stationHost.secureStorage`). */
export interface ElectronSecureStorageApi {
  isAvailable(): Promise<boolean>;
  /** Returns base64 ciphertext. */
  encrypt(plain: string): Promise<string>;
  decrypt(base64: string): Promise<string>;
}

/** Keeps somewhere to put ciphertext (defaults to `localStorage`). */
export interface KeyValueArea {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class SecureStorageUnavailableError extends Error {
  constructor() {
    super('This computer cannot encrypt the station credentials (OS secure storage is off)');
    this.name = 'SecureStorageUnavailableError';
  }
}

export class ElectronSecureStore implements SecureStore {
  readonly kind = 'electron' as const;
  readonly devOnly = false;

  constructor(
    private readonly api: ElectronSecureStorageApi,
    private readonly area: KeyValueArea = globalThis.localStorage,
    private readonly prefix = 'enp.secure.',
  ) {}

  async get(key: string) {
    const cipher = this.area.getItem(this.prefix + key);
    if (cipher === null) return null;
    return this.api.decrypt(cipher);
  }

  async set(key: string, value: string) {
    if (!(await this.api.isAvailable())) throw new SecureStorageUnavailableError();
    this.area.setItem(this.prefix + key, await this.api.encrypt(value));
  }

  async delete(key: string) {
    this.area.removeItem(this.prefix + key);
  }
}

/**
 * The calls the Keychain (iOS) / Keystore (Android) plugin makes available: the string API of
 * `@aparajita/capacitor-secure-storage` (chosen in WP3.4: maintained for Capacitor 8, and on
 * iOS it lets us keep the item on this device only; see shells/README.md).
 */
export interface KeychainPlugin {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  /** iCloud Keychain sync (iOS). Always turned off: a device secret stays on its device. */
  setSynchronize?(sync: boolean): Promise<void>;
  /** iOS Keychain accessibility for new items (the plugin's `KeychainAccess` value). */
  setDefaultKeychainAccess?(access: number): Promise<void>;
  setKeyPrefix?(prefix: string): Promise<void>;
}

export interface CapacitorSecureStoreOptions {
  /**
   * iOS accessibility: `afterFirstUnlockThisDeviceOnly` (3), so the secret is readable after
   * the first unlock since boot (a kiosk iPad restarts unattended) and never restored onto
   * another device from a backup.
   */
  keychainAccess?: number;
  /** Key prefix inside the Keychain/SharedPreferences. Default `enp.`. */
  prefix?: string;
}

/** iOS `kSecAttrAccessibleAfterFirstUnlockThisDeviceOnly` in the plugin's `KeychainAccess`. */
export const KEYCHAIN_AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY = 3;

export class CapacitorSecureStore implements SecureStore {
  readonly kind = 'capacitor' as const;
  readonly devOnly = false;
  #ready: Promise<void> | undefined;

  constructor(
    private readonly plugin: KeychainPlugin,
    private readonly options: CapacitorSecureStoreOptions = {},
  ) {}

  #init(): Promise<void> {
    this.#ready ??= (async () => {
      await this.plugin.setKeyPrefix?.(this.options.prefix ?? 'enp.');
      await this.plugin.setSynchronize?.(false);
      await this.plugin.setDefaultKeychainAccess?.(
        this.options.keychainAccess ?? KEYCHAIN_AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY,
      );
    })();
    return this.#ready;
  }

  async get(key: string) {
    await this.#init();
    return (await this.plugin.getItem(key)) ?? null;
  }
  async set(key: string, value: string) {
    await this.#init();
    await this.plugin.setItem(key, value);
  }
  async delete(key: string) {
    await this.#init();
    await this.plugin.removeItem(key);
  }
}

/** A native station stored its credentials somewhere that does not protect them. */
export class InsecureSecretStoreError extends Error {
  constructor(platform: string, kind: string) {
    super(`A ${platform} station cannot keep its credentials in ${kind} (development only)`);
    this.name = 'InsecureSecretStoreError';
  }
}

/**
 * Native builds must use OS secure storage: the IndexedDB (and in-memory) stores are for
 * browsers and tests only. Throws for a native platform with a dev-only store.
 */
export function assertSecureStoreFor(platform: string, store: SecureStore): void {
  if (platform !== 'web' && store.devOnly) throw new InsecureSecretStoreError(platform, store.kind);
}

/** DEVELOPMENT ONLY: the value is stored in clear in IndexedDB (see the header). */
export class IndexedDbSecureStore implements SecureStore {
  readonly kind = 'indexeddb-dev' as const;
  readonly devOnly = true;
  #db: Promise<IDBDatabase> | undefined;

  constructor(private readonly dbName = 'enp-station-dev-secrets') {}

  #open(): Promise<IDBDatabase> {
    this.#db ??= new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = () => req.result.createObjectStore('kv');
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
    return this.#db;
  }

  async #run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>) {
    const db = await this.#open();
    return new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction('kv', mode).objectStore('kv'));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
    });
  }

  async get(key: string) {
    const value = await this.#run<unknown>('readonly', (s) => s.get(key));
    return typeof value === 'string' ? value : null;
  }
  async set(key: string, value: string) {
    await this.#run('readwrite', (s) => s.put(value, key));
  }
  async delete(key: string) {
    await this.#run('readwrite', (s) => s.delete(key));
  }
}
